import React, { Component, Fragment } from 'react';
import { Layout, Typography, Card, Row, Col, Form, Input, InputNumber, DatePicker, Button, Switch, Select, message, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import AdminPortalHeader from '../../components/common/admin_portal_header';
import { createCorporateDiscount, getPlans } from '../../api/pets-api';
import moment from 'moment';

const { Paragraph, Title } = Typography;
const { Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;

class AdminCorporateDiscountCreate extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            plans: [],
            plansLoading: false
        };
        this.formRef = React.createRef();
    }

    componentDidMount() {
        this.fetchPlans();
    }

    fetchPlans = async () => {
        this.setState({ plansLoading: true });
        try {
            const response = await getPlans();
            if (response.status === 200) {
                this.setState({ 
                    plans: response.data.result || [],
                    plansLoading: false 
                });
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            this.setState({ plansLoading: false });
        }
    };

    handleSubmit = async (values) => {
        this.setState({ loading: true });
        try {
            const data = {
                companyName: values.companyName,
                emailPattern: values.emailPattern,
                discountPercentage: values.discountPercentage,
                startDate: values.startDate.format('YYYY-MM-DD'),
                endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
                isActive: values.isActive !== undefined ? values.isActive : true,
                description: values.description || null,
                contactPerson: values.contactPerson || null,
                contactEmail: values.contactEmail || null,
                contactPhone: values.contactPhone || null,
                maxUsageCount: values.maxUsageCount || null,
                appliedToPlanIds: values.appliedToPlanIds || []
            };

            const response = await createCorporateDiscount(data);
            
            if (response.status === 201 && response.data.status === 'ok') {
                message.success('Corporate discount created successfully');
                this.props.history.push('/admin_corporate_discounts');
            } else {
                message.error(response.data.message || 'Failed to create discount');
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error('Error creating discount:', error);
            message.error(error.response?.data?.message || 'Error creating corporate discount');
            this.setState({ loading: false });
        }
    };

    handleCancel = () => {
        this.props.history.push('/admin_corporate_discounts');
    };

    validateEmailPattern = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('Please enter email pattern'));
        }
        
        // Basic regex validation for email patterns
        const emailPatternRegex = /^[a-zA-Z0-9@._*-]+$/;
        if (!emailPatternRegex.test(value)) {
            return Promise.reject(new Error('Invalid email pattern format'));
        }
        
        return Promise.resolve();
    };

    disabledEndDate = (current) => {
        const startDate = this.formRef.current?.getFieldValue('startDate');
        if (!startDate) {
            return false;
        }
        return current && current <= startDate.startOf('day');
    };

    render() {
        const { loading, plans, plansLoading } = this.state;

        return (
            <Fragment>
                <Layout className="main-layout dashboard-layout">
                    <AdminPortalHeader />
                    <Content className="dashboard-content">
                        <div className="container-fluid">
                            <Row gutter={[15, 15]} className="mb-4">
                                <Col xs={24}>
                                    <Card>
                                        <Button
                                            type="link"
                                            icon={<ArrowLeftOutlined />}
                                            onClick={this.handleCancel}
                                            style={{ padding: 0, marginBottom: 10 }}
                                        >
                                            Back to Discounts
                                        </Button>
                                        <Title level={3} className="mb-0">Create New Corporate Discount</Title>
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[15, 15]}>
                                <Col xs={24}>
                                    <Card>
                                        <Spin spinning={loading}>
                                            <Form
                                                ref={this.formRef}
                                                layout="vertical"
                                                onFinish={this.handleSubmit}
                                                initialValues={{
                                                    isActive: true,
                                                    discountPercentage: 10
                                                }}
                                            >
                                                <Row gutter={[15, 15]}>
                                                    <Col xs={24} md={12}>
                                                        <Form.Item
                                                            name="companyName"
                                                            label="Company Name"
                                                            rules={[{ required: true, message: 'Please enter company name' }]}
                                                        >
                                                            <Input placeholder="Enter company name" size="large" />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={12}>
                                                        <Form.Item
                                                            name="emailPattern"
                                                            label="Email Pattern"
                                                            rules={[
                                                                { required: true, message: 'Please enter email pattern' },
                                                                { validator: this.validateEmailPattern }
                                                            ]}
                                                            extra="e.g., @company.com or *@company.com"
                                                        >
                                                            <Input placeholder="Enter email pattern" size="large" />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={12}>
                                                        <Form.Item
                                                            name="startDate"
                                                            label="Start Date"
                                                            rules={[{ required: true, message: 'Please select start date' }]}
                                                        >
                                                            <DatePicker 
                                                                size="large" 
                                                                style={{ width: '100%' }}
                                                                format="DD/MM/YYYY"
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={12}>
                                                        <Form.Item
                                                            name="endDate"
                                                            label="End Date (Optional)"
                                                        >
                                                            <DatePicker 
                                                                size="large" 
                                                                style={{ width: '100%' }}
                                                                format="DD/MM/YYYY"
                                                                disabledDate={this.disabledEndDate}
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={8}>
                                                        <Form.Item
                                                            name="discountPercentage"
                                                            label="Discount Percentage"
                                                            rules={[
                                                                { required: true, message: 'Please enter discount percentage' },
                                                                { 
                                                                    validator: (_, value) => {
                                                                        if (value === null || value === undefined || value === '') {
                                                                            return Promise.resolve();
                                                                        }
                                                                        if (value >= 0.01 && value <= 100) {
                                                                            return Promise.resolve();
                                                                        }
                                                                        return Promise.reject(new Error('Must be between 0.01 and 100'));
                                                                    }
                                                                }
                                                            ]}
                                                        >
                                                            <InputNumber
                                                                placeholder="Enter discount %"
                                                                size="large"
                                                                style={{ width: '100%' }}
                                                                min={0.01}
                                                                max={100}
                                                                step={0.01}
                                                                precision={2}
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={8}>
                                                        <Form.Item
                                                            name="maxUsageCount"
                                                            label="Maximum Usage Count (Optional)"
                                                            extra="Leave empty for unlimited usage"
                                                        >
                                                            <InputNumber
                                                                placeholder="Enter max usage count"
                                                                size="large"
                                                                style={{ width: '100%' }}
                                                                min={1}
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={8}>
                                                        <Form.Item
                                                            name="appliedToPlanIds"
                                                            label="Apply to Plans (Optional)"
                                                            extra="Select plans or leave empty for all plans"
                                                        >
                                                            <Select
                                                                mode="multiple"
                                                                placeholder="Select plans"
                                                                size="large"
                                                                loading={plansLoading}
                                                            >
                                                                {plans.map(plan => (
                                                                    <Option key={plan.id} value={plan.id}>
                                                                        {plan.title}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24}>
                                                        <Form.Item
                                                            name="description"
                                                            label="Description (Optional)"
                                                        >
                                                            <TextArea 
                                                                rows={4} 
                                                                placeholder="Enter discount description"
                                                                size="large"
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24}>
                                                        <Title level={5}>Contact Information (Optional)</Title>
                                                    </Col>

                                                    <Col xs={24} md={8}>
                                                        <Form.Item
                                                            name="contactPerson"
                                                            label="Contact Person"
                                                        >
                                                            <Input placeholder="Enter contact person name" size="large" />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={8}>
                                                        <Form.Item
                                                            name="contactEmail"
                                                            label="Contact Email"
                                                            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                                                        >
                                                            <Input placeholder="Enter contact email" size="large" />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24} md={8}>
                                                        <Form.Item
                                                            name="contactPhone"
                                                            label="Contact Phone"
                                                        >
                                                            <Input placeholder="Enter contact phone" size="large" />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24}>
                                                        <Form.Item
                                                            name="isActive"
                                                            label="Status"
                                                            valuePropName="checked"
                                                        >
                                                            <Switch 
                                                                checkedChildren="Active" 
                                                                unCheckedChildren="Inactive"
                                                            />
                                                        </Form.Item>
                                                    </Col>

                                                    <Col xs={24}>
                                                        <Form.Item>
                                                            <Button
                                                                type="primary"
                                                                htmlType="submit"
                                                                size="large"
                                                                loading={loading}
                                                                style={{ marginRight: 10 }}
                                                            >
                                                                Create Discount
                                                            </Button>
                                                            <Button
                                                                size="large"
                                                                onClick={this.handleCancel}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Form>
                                        </Spin>
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    </Content>
                </Layout>
            </Fragment>
        );
    }
}

export default AdminCorporateDiscountCreate;
